// Cave84 — Wishlist (Phase 5 Week 15)
// Spec §4.9 F9 + §6.3 Screen 6.
// wine_id is now optional: entries may use wine_name_free when wine not yet in catalog.

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { serverTimestamp } from 'firebase/firestore';
import type {
  WishlistEntry, WishlistPriority, WishlistStatus,
  BottleFormat, Currency, PurchaseSource, Vintage, WineStyle,
} from '@cave84/shared-types';
import { useAuthStore } from '@/stores/auth.store';
import { useWines, useCreateWine } from '@/hooks/useWines';
import { useProducersMap, useProducerSearch, useCreateProducer } from '@/hooks/useProducers';
import { useAppellationSearch, useCreateAppellation } from '@/hooks/useAppellations';
import { useCellars } from '@/hooks/useCellars';
import { useCreateBottles } from '@/hooks/useBottles';
import {
  useActiveWishlistEntries,
  useWishlistEntries,
  useCreateWishlistEntry,
  useUpdateWishlistEntry,
  useDeleteWishlistEntry,
  useMarkWishlistAcquired,
} from '@/hooks/useWishlist';
import type { WishlistCreateInput } from '@/services/wishlist';
import { findVintageByWineAndYear, createVintage } from '@/services/vintages';
import { Money } from '@/components/Money';
import { SOURCE_LABELS } from '@/services/bottleLabels';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Lowercase + strip diacritics so "Echezeaux" matches "Echezeaux". */
function normalizeStr(s: string): string {
  return s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const PRIORITY_BADGE: Record<WishlistPriority, { label: string; cls: string }> = {
  high:   { label: 'High',   cls: 'bg-red-900/60 text-red-300 border-red-700' },
  medium: { label: 'Medium', cls: 'bg-amber-900/50 text-amber-300 border-amber-700' },
  low:    { label: 'Low',    cls: 'bg-surface-3 text-text-muted border-border-medium' },
};

const STATUS_BADGE: Record<WishlistStatus, { label: string; cls: string }> = {
  active:    { label: 'Active',    cls: 'text-emerald-400' },
  acquired:  { label: 'Acquired',  cls: 'text-sky-400' },
  abandoned: { label: 'Abandoned', cls: 'text-text-muted line-through' },
};

type SortKey = 'priority' | 'date_added' | 'target_price' | 'wine_name';
type GroupKey = 'none' | 'status' | 'producer';

const PRIORITY_ORDER: Record<WishlistPriority, number> = { high: 0, medium: 1, low: 2 };

// ---------------------------------------------------------------------------
// Acquire modal
// Opens when user clicks "Mark Acquired". Collects purchase details, creates
// vintage + bottles, then marks the wishlist entry as acquired.
// For free-text entries (not in catalog): runs a 3-step wizard to create the
// wine in the catalog, add bottles to inventory, then DELETE the wishlist entry.
// ---------------------------------------------------------------------------

const FORMAT_LABELS: Record<BottleFormat, string> = {
  half: 'Half (375ml)',
  '750ml': 'Standard (750ml)',
  magnum: 'Magnum (1.5L)',
  double_magnum: 'Double Magnum (3L)',
  jeroboam: 'Jeroboam (4.5L)',
  imperial: 'Imperiale (6L)',
  salmanazar: 'Salmanazar (9L)',
  methuselah: 'Methuselah (6L Champ.)',
  other: 'Other',
};

const CURRENCIES: Currency[] = ['EUR', 'USD', 'ILS', 'GBP', 'CHF'];

const WINE_STYLES: WineStyle[] = ['red', 'white', 'rose', 'sparkling', 'fortified', 'sweet', 'orange'];

const STYLE_LABELS: Record<WineStyle, string> = {
  red: 'Red', white: 'White', rose: 'Rose', sparkling: 'Sparkling',
  fortified: 'Fortified', sweet: 'Sweet', orange: 'Orange',
};

const COMMON_FORMATS: BottleFormat[] = ['750ml', 'magnum', 'half', 'double_magnum'];

interface AcquireModalProps {
  entry: WishlistEntry;
  wineName: string;
  producerName: string;
  wineId: string | undefined;
  onClose: () => void;
}

function AcquireModal({ entry, wineName, producerName, wineId, onClose }: AcquireModalProps) {
  const { data: cellars = [] } = useCellars();
  const createBottlesMutation = useCreateBottles();
  const markAcquiredMutation = useMarkWishlistAcquired();
  const deleteEntryMutation = useDeleteWishlistEntry();

  const isFreeText = !wineId;

  // Wizard step (free-text path only: 1=Producer, 2=Wine details, 3=Bottle form)
  const [wizStep, setWizStep] = useState<1 | 2 | 3>(1);

  // ── Step 1: Producer ──────────────────────────────────────────────────────
  const [producerSearch, setProducerSearch] = useState('');
  const [selectedProducerId, setSelectedProducerId] = useState('');
  const [showProducerDrop, setShowProducerDrop] = useState(false);
  const [newProducerName, setNewProducerName] = useState('');
  const [newProducerCountry, setNewProducerCountry] = useState('FR');
  const [newProducerRegion, setNewProducerRegion] = useState('');
  const producerResults = useProducerSearch(producerSearch);
  const createProducerMutation = useCreateProducer();
  const selectedProducerLabel = selectedProducerId
    ? (producerResults.find(p => p.id === selectedProducerId)?.name ?? producerSearch)
    : '';

  // ── Step 2: Wine details ──────────────────────────────────────────────────
  const [cuveeName, setCuveeName] = useState(entry.wine_name_free ?? '');
  const [wineStyle, setWineStyle] = useState<WineStyle>('red');
  const [appellationSearch, setAppellationSearch] = useState('');
  const [selectedAppellationId, setSelectedAppellationId] = useState('');
  const [showAppellationDrop, setShowAppellationDrop] = useState(false);
  const [newAppellationName, setNewAppellationName] = useState('');
  const [newAppellationPath, setNewAppellationPath] = useState('');
  const appellationResults = useAppellationSearch(appellationSearch);
  const createWineMutation = useCreateWine();
  const createAppellationMutation = useCreateAppellation();
  const selectedAppellationLabel = selectedAppellationId
    ? (appellationResults.find(a => a.id === selectedAppellationId)?.name ?? appellationSearch)
    : '';

  // ── Bottle form fields (Step 3 + catalog path) ────────────────────────────
  const [vintageYear, setVintageYear] = useState(entry.vintage_year?.toString() ?? '');
  const [format, setFormat] = useState<BottleFormat>('750ml');
  const [qty, setQty] = useState('1');
  const [costLanded, setCostLanded] = useState(entry.target_price?.toString() ?? '');
  const [currency, setCurrency] = useState<Currency>((entry.target_currency as Currency) ?? 'EUR');
  const [source, setSource] = useState<PurchaseSource>('other');
  const [sourceDetail, setSourceDetail] = useState('');
  const [purchaseDate, setPurchaseDate] = useState(new Date().toISOString().slice(0, 10));
  const [cellarId, setCellarId] = useState('');
  const [notes, setNotes] = useState(entry.notes ?? '');
  const [submitError, setSubmitError] = useState('');

  const resolvedCellarId = cellarId || cellars[0]?.id || '';
  const qtyNum = Math.max(1, parseInt(qty) || 1);

  const isPendingAll =
    createProducerMutation.isPending || createAppellationMutation.isPending ||
    createWineMutation.isPending || createBottlesMutation.isPending ||
    markAcquiredMutation.isPending || deleteEntryMutation.isPending;

  // ── Catalog path submit ───────────────────────────────────────────────────
  async function handleCatalogSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitError('');
    if (!wineId) return;
    try {
      const year = parseInt(vintageYear);
      if (!year || year < 1900 || year > new Date().getFullYear()) {
        setSubmitError('Please enter a valid vintage year.'); return;
      }
      if (!resolvedCellarId) { setSubmitError('Please select a cellar.'); return; }
      const cost = parseFloat(costLanded);
      if (!cost || cost <= 0) { setSubmitError('Please enter a valid purchase price.'); return; }

      let vintage = await findVintageByWineAndYear(wineId, year);
      if (!vintage) {
        const defaultWindow: Vintage['drinking_window_initial'] = {
          from_year: year + 3, peak_from: year + 6, peak_to: year + 12, to_year: year + 18,
          status: 'developing',
          assessed_at: serverTimestamp() as Vintage['drinking_window_initial']['assessed_at'],
          assessed_by: 'manual',
        };
        vintage = await createVintage({
          wine_id: wineId, year,
          vintage_quality_rating: 3,
          drinking_window_initial: defaultWindow,
          drinking_window_current: defaultWindow,
        });
      }

      const bottleIds = await createBottlesMutation.mutateAsync({
        input: {
          vintage_id: vintage.id,
          wine_id: wineId,
          format,
          purchase_date: new Date(purchaseDate),
          purchase_source: source,
          purchase_source_detail: sourceDetail.trim() || undefined,
          cost_landed: cost,
          cost_currency: currency,
          cost_eur_at_purchase: cost,
          current_cellar_id: resolvedCellarId,
          is_special_occasion: false,
          fill_level: 'into_neck',
          label_condition: 'perfect',
          capsule_condition: 'perfect',
          in_OWC: false,
          notes: notes.trim() || undefined,
        },
        qty: qtyNum,
      });

      await markAcquiredMutation.mutateAsync({ id: entry.id, bottleIds });
      onClose();
    } catch (err) {
      console.error('[AcquireModal] catalog submit failed:', err);
      setSubmitError(err instanceof Error ? err.message : 'Failed to save. Check your connection and try again.');
    }
  }

  // ── Free-text final submit (step 3) ──────────────────────────────────────
  async function handleFreeTextSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitError('');
    try {
      const year = parseInt(vintageYear);
      if (!year || year < 1900 || year > new Date().getFullYear()) {
        setSubmitError('Please enter a valid vintage year.'); return;
      }
      if (!resolvedCellarId) { setSubmitError('Please select a cellar.'); return; }

      // 1. Producer
      let producerId = selectedProducerId;
      if (!producerId) {
        const pName = (newProducerName.trim() || producerSearch.trim());
        if (!pName) { setSubmitError('Enter a producer name.'); return; }
        const p = await createProducerMutation.mutateAsync({
          name: pName,
          aliases: [],
          country: newProducerCountry.trim() || 'FR',
          region: newProducerRegion.trim() || 'Unknown',
          top_cuvees: [],
        });
        producerId = p.id;
      }

      // 2. Appellation
      let appellationId = selectedAppellationId;
      if (!appellationId) {
        const appName = (newAppellationName.trim() || appellationSearch.trim());
        if (!appName) { setSubmitError('Enter an appellation name.'); return; }
        const a = await createAppellationMutation.mutateAsync({
          name: appName,
          hierarchy_path: newAppellationPath.trim() || appName,
          classification_level: 'regional',
          country: newProducerCountry.trim() || 'FR',
          region: newProducerRegion.trim() || 'Unknown',
        });
        appellationId = a.id;
      }

      // 3. Wine
      const wine = await createWineMutation.mutateAsync({
        producer_id: producerId,
        appellation_id: appellationId,
        cuvee_name: cuveeName.trim() || undefined,
        style: wineStyle,
        grape_composition: [],
      });

      // 4. Vintage
      const defaultWindow: Vintage['drinking_window_initial'] = {
        from_year: year + 3, peak_from: year + 6, peak_to: year + 12, to_year: year + 18,
        status: 'developing',
        assessed_at: serverTimestamp() as Vintage['drinking_window_initial']['assessed_at'],
        assessed_by: 'manual',
      };
      const vintage = await createVintage({
        wine_id: wine.id, year,
        vintage_quality_rating: 3,
        drinking_window_initial: defaultWindow,
        drinking_window_current: defaultWindow,
      });

      // 5. Bottles
      await createBottlesMutation.mutateAsync({
        input: {
          vintage_id: vintage.id,
          wine_id: wine.id,
          format,
          purchase_date: new Date(purchaseDate),
          purchase_source: source,
          purchase_source_detail: sourceDetail.trim() || undefined,
          cost_landed: parseFloat(costLanded) || 0,
          cost_currency: currency,
          cost_eur_at_purchase: parseFloat(costLanded) || 0,
          current_cellar_id: resolvedCellarId,
          is_special_occasion: false,
          fill_level: 'into_neck',
          label_condition: 'perfect',
          capsule_condition: 'perfect',
          in_OWC: false,
          notes: notes.trim() || undefined,
        },
        qty: qtyNum,
      });

      // 6. Delete wishlist entry (wine didn't exist before — nothing to mark acquired)
      await deleteEntryMutation.mutateAsync(entry.id);
      onClose();
    } catch (err) {
      console.error('[AcquireModal] freetext submit failed:', err);
      setSubmitError(err instanceof Error ? err.message : 'Failed to save. Check your connection and try again.');
    }
  }

  // ── Step progress bar ─────────────────────────────────────────────────────
  // Defined as render function (not component) to avoid remount-on-rerender focus loss.
  const renderStepBar = (current: number) => (
    <div className="flex gap-1.5 mb-1">
      {[1, 2, 3].map(s => (
        <div key={s} className={`flex-1 h-1 rounded-full transition-colors ${s <= current ? 'bg-sky-600' : 'bg-surface-3'}`} />
      ))}
    </div>
  );

  // ── Bottle form fields (reused in catalog path + step 3) ─────────────────
  // Defined as render function (not component) to avoid remount-on-rerender focus loss.
  const renderBottleFields = () => {
    return (
      <>
        <div>
          <span className="block text-xs text-text-muted mb-1">Vintage year *</span>
          <input
            type="number" value={vintageYear} onChange={e => setVintageYear(e.target.value)}
            placeholder="e.g. 2015" min={1900} max={new Date().getFullYear()}
            className="w-full px-3 py-2 rounded-md border border-border-medium bg-surface-0 text-text-primary placeholder:text-text-muted focus:outline-none focus:border-gold text-sm"
          />
        </div>

        <div>
          <span className="block text-xs text-text-muted mb-1">Format</span>
          <div className="flex flex-wrap gap-2">
            {COMMON_FORMATS.map(f => (
              <button key={f} type="button" onClick={() => setFormat(f)}
                className={'px-2.5 py-1 rounded border text-xs transition-colors ' + (
                  format === f
                    ? 'border-gold bg-gold/10 text-gold'
                    : 'border-border-subtle bg-surface-2 text-text-muted hover:border-border-medium'
                )}>
                {FORMAT_LABELS[f]}
              </button>
            ))}
          </div>
          <select
            value={COMMON_FORMATS.includes(format) ? '' : format}
            onChange={e => { if (e.target.value) setFormat(e.target.value as BottleFormat); }}
            className="mt-2 w-full px-2 py-1.5 rounded border border-border-medium bg-surface-0 text-text-muted text-xs focus:outline-none focus:border-gold">
            <option value="">Other format...</option>
            {(['jeroboam', 'imperial', 'salmanazar', 'methuselah', 'other'] as BottleFormat[]).map(f => (
              <option key={f} value={f}>{FORMAT_LABELS[f]}</option>
            ))}
          </select>
        </div>

        <div>
          <span className="block text-xs text-text-muted mb-1">Quantity</span>
          <input
            type="number" value={qty} onChange={e => setQty(e.target.value)} min={1} max={120}
            className="w-full px-3 py-2 rounded-md border border-border-medium bg-surface-0 text-text-primary focus:outline-none focus:border-gold text-sm"
          />
        </div>

        <div className="flex gap-3">
          <div className="flex-1">
            <span className="block text-xs text-text-muted mb-1">Purchase price (optional)</span>
            <input
              type="number" value={costLanded} onChange={e => setCostLanded(e.target.value)}
              placeholder="e.g. 350" min={0} step={1}
              className="w-full px-3 py-2 rounded-md border border-border-medium bg-surface-0 text-text-primary placeholder:text-text-muted focus:outline-none focus:border-gold text-sm"
            />
          </div>
          <div className="w-24">
            <span className="block text-xs text-text-muted mb-1">Currency</span>
            <select value={currency} onChange={e => setCurrency(e.target.value as Currency)}
              className="w-full px-2 py-2 rounded-md border border-border-medium bg-surface-0 text-text-primary focus:outline-none focus:border-gold text-sm">
              {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>

        <div>
          <span className="block text-xs text-text-muted mb-1">Purchase source</span>
          <select value={source} onChange={e => setSource(e.target.value as PurchaseSource)}
            className="w-full px-3 py-2 rounded-md border border-border-medium bg-surface-0 text-text-primary focus:outline-none focus:border-gold text-sm">
            {(Object.keys(SOURCE_LABELS) as PurchaseSource[]).map(s => (
              <option key={s} value={s}>{SOURCE_LABELS[s]}</option>
            ))}
          </select>
        </div>

        <div>
          <span className="block text-xs text-text-muted mb-1">Source detail (optional)</span>
          <input
            type="text" value={sourceDetail} onChange={e => setSourceDetail(e.target.value)}
            placeholder="e.g. Lot 42, MWC Spring 2026"
            className="w-full px-3 py-2 rounded-md border border-border-medium bg-surface-0 text-text-primary placeholder:text-text-muted focus:outline-none focus:border-gold text-sm"
          />
        </div>

        <div>
          <span className="block text-xs text-text-muted mb-1">Purchase date</span>
          <input
            type="date" value={purchaseDate} onChange={e => setPurchaseDate(e.target.value)}
            className="w-full px-3 py-2 rounded-md border border-border-medium bg-surface-0 text-text-primary focus:outline-none focus:border-gold text-sm"
          />
        </div>

        <div>
          <span className="block text-xs text-text-muted mb-1">Cellar *</span>
          {cellars.length === 0
            ? <p className="text-xs text-red-400">No cellars found. Create a cellar first.</p>
            : (
              <select value={resolvedCellarId} onChange={e => setCellarId(e.target.value)}
                className="w-full px-3 py-2 rounded-md border border-border-medium bg-surface-0 text-text-primary focus:outline-none focus:border-gold text-sm">
                {cellars.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            )
          }
        </div>

        <div>
          <span className="block text-xs text-text-muted mb-1">Notes (optional)</span>
          <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2}
            placeholder="e.g. Same case as the 2018s, excellent condition"
            className="w-full px-3 py-2 rounded-md border border-border-medium bg-surface-0 text-text-primary placeholder:text-text-muted focus:outline-none focus:border-gold text-sm resize-none">
          </textarea>
        </div>
      </>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog">
      <div role="button" tabIndex={0} aria-label="Close" className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClose(); } }} />
      <div className="relative z-10 w-full max-w-md bg-surface-1 border border-border-medium rounded-xl shadow-2xl max-h-[90vh] overflow-y-auto">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border-subtle sticky top-0 bg-surface-1 z-10">
          <div>
            <h2 className="text-sm font-semibold text-text-primary">
              {isFreeText ? `Add to catalog — step ${wizStep} of 3` : 'Mark as Acquired'}
            </h2>
            <p className="text-xs text-text-muted mt-0.5 truncate max-w-xs">
              {producerName ? producerName + ' — ' : ''}{wineName}
            </p>
          </div>
          <button type="button" onClick={onClose} className="text-text-muted hover:text-text-primary flex-shrink-0 ml-3">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* ── Catalog path ──────────────────────────────────────────────── */}
        {!isFreeText && (
          <form onSubmit={handleCatalogSubmit} className="px-5 py-4 space-y-4">
            {renderBottleFields()}

            {submitError && (
              <p className="text-xs text-red-400 bg-red-900/20 border border-red-800 rounded px-3 py-2">{submitError}</p>
            )}

            <div className="flex gap-3 pt-1">
              <button type="button" onClick={onClose}
                className="flex-1 py-2 rounded-lg border border-border-medium bg-surface-2 text-text-secondary text-sm hover:border-border-strong transition-colors">
                Cancel
              </button>
              <button type="submit" disabled={!vintageYear || !resolvedCellarId || isPendingAll}
                className="flex-1 py-2 rounded-lg bg-sky-700 text-white text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-40">
                {isPendingAll ? 'Adding...' : `Add ${qtyNum} bottle${qtyNum !== 1 ? 's' : ''} to inventory`}
              </button>
            </div>
          </form>
        )}

        {/* ── Free-text: Step 1 — Producer ─────────────────────────────── */}
        {isFreeText && wizStep === 1 && (
          <div className="px-5 py-4 space-y-4">
            {renderStepBar(1)}
            <p className="text-xs text-text-muted leading-relaxed">
              This wine is not yet in your catalog. Find or create the producer first.
            </p>

            <div className="relative">
              <span className="block text-xs text-text-muted mb-1">Search producer *</span>
              {selectedProducerId ? (
                <div className="flex items-center gap-2">
                  <span className="flex-1 px-3 py-2 rounded-md border border-gold bg-gold/10 text-gold text-sm truncate">
                    {selectedProducerLabel}
                  </span>
                  <button type="button"
                    onClick={() => { setSelectedProducerId(''); setProducerSearch(''); }}
                    className="text-text-muted hover:text-text-primary text-sm px-2 py-2">
                    ✕
                  </button>
                </div>
              ) : (
                <>
                  <input
                    type="text" value={producerSearch}
                    onChange={e => { setProducerSearch(e.target.value); setShowProducerDrop(true); }}
                    onFocus={() => setShowProducerDrop(true)}
                    onBlur={() => setTimeout(() => setShowProducerDrop(false), 150)}
                    placeholder="e.g. Rousseau, Leflaive..."
                    className="w-full px-3 py-2 rounded-md border border-border-medium bg-surface-0 text-text-primary placeholder:text-text-muted focus:outline-none focus:border-gold text-sm"
                  />
                  {showProducerDrop && producerResults.length > 0 && (
                    <ul className="absolute z-10 mt-1 w-full bg-surface-2 border border-border-medium rounded-md shadow-lg max-h-40 overflow-y-auto">
                      {producerResults.slice(0, 6).map(p => (
                        <li key={p.id}>
                          <button type="button"
                            onMouseDown={() => { setSelectedProducerId(p.id); setProducerSearch(p.name); setShowProducerDrop(false); }}
                            className="w-full text-left px-3 py-2 hover:bg-surface-3 text-sm">
                            <span className="text-text-primary">{p.name}</span>
                            <span className="text-text-muted ml-2 text-xs">{p.region}, {p.country}</span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              )}
            </div>

            {/* Create new producer — shown when search has text but no results */}
            {!selectedProducerId && producerSearch.trim().length >= 2 && producerResults.length === 0 && (
              <div className="border border-border-medium rounded-lg px-3 py-3 space-y-3 bg-surface-2">
                <p className="text-xs text-amber-400 font-medium">No producer found — fill in details to create one:</p>
                <div>
                  <span className="block text-xs text-text-muted mb-1">Producer name *</span>
                  <input type="text" value={newProducerName}
                    onChange={e => setNewProducerName(e.target.value)}
                    placeholder={producerSearch}
                    className="w-full px-3 py-2 rounded-md border border-border-medium bg-surface-0 text-text-primary placeholder:text-text-muted focus:outline-none focus:border-gold text-sm"
                  />
                </div>
                <div className="flex gap-2">
                  <div className="flex-1">
                    <span className="block text-xs text-text-muted mb-1">Country (ISO-2)</span>
                    <input type="text" value={newProducerCountry}
                      onChange={e => setNewProducerCountry(e.target.value)}
                      placeholder="FR"
                      className="w-full px-3 py-2 rounded-md border border-border-medium bg-surface-0 text-text-primary placeholder:text-text-muted focus:outline-none focus:border-gold text-sm"
                    />
                  </div>
                  <div className="flex-1">
                    <span className="block text-xs text-text-muted mb-1">Region</span>
                    <input type="text" value={newProducerRegion}
                      onChange={e => setNewProducerRegion(e.target.value)}
                      placeholder="e.g. Burgundy"
                      className="w-full px-3 py-2 rounded-md border border-border-medium bg-surface-0 text-text-primary placeholder:text-text-muted focus:outline-none focus:border-gold text-sm"
                    />
                  </div>
                </div>
              </div>
            )}

            {submitError && (
              <p className="text-xs text-red-400 bg-red-900/20 border border-red-800 rounded px-3 py-2">{submitError}</p>
            )}

            <div className="flex gap-3 pt-1">
              <button type="button" onClick={onClose}
                className="flex-1 py-2 rounded-lg border border-border-medium bg-surface-2 text-text-secondary text-sm hover:border-border-strong transition-colors">
                Cancel
              </button>
              <button type="button"
                disabled={!selectedProducerId && producerSearch.trim().length < 2}
                onClick={() => { setSubmitError(''); setWizStep(2); }}
                className="flex-1 py-2 rounded-lg bg-sky-700 text-white text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-40">
                Next
              </button>
            </div>
          </div>
        )}

        {/* ── Free-text: Step 2 — Wine details ─────────────────────────── */}
        {isFreeText && wizStep === 2 && (
          <div className="px-5 py-4 space-y-4">
            {renderStepBar(2)}

            <div>
              <span className="block text-xs text-text-muted mb-1">Cuvee name</span>
              <input type="text" value={cuveeName} onChange={e => setCuveeName(e.target.value)}
                placeholder="e.g. Chambertin, Meursault Perrieres..."
                className="w-full px-3 py-2 rounded-md border border-border-medium bg-surface-0 text-text-primary placeholder:text-text-muted focus:outline-none focus:border-gold text-sm"
              />
            </div>

            <div>
              <span className="block text-xs text-text-muted mb-1">Style *</span>
              <div className="flex flex-wrap gap-2">
                {WINE_STYLES.map(s => (
                  <button key={s} type="button" onClick={() => setWineStyle(s)}
                    className={'px-2.5 py-1 rounded border text-xs transition-colors ' + (
                      wineStyle === s
                        ? 'border-gold bg-gold/10 text-gold'
                        : 'border-border-subtle bg-surface-2 text-text-muted hover:border-border-medium'
                    )}>
                    {STYLE_LABELS[s]}
                  </button>
                ))}
              </div>
            </div>

            <div className="relative">
              <span className="block text-xs text-text-muted mb-1">Appellation *</span>
              {selectedAppellationId ? (
                <div className="flex items-center gap-2">
                  <span className="flex-1 px-3 py-2 rounded-md border border-gold bg-gold/10 text-gold text-sm truncate">
                    {selectedAppellationLabel}
                  </span>
                  <button type="button"
                    onClick={() => { setSelectedAppellationId(''); setAppellationSearch(''); }}
                    className="text-text-muted hover:text-text-primary text-sm px-2 py-2">
                    ✕
                  </button>
                </div>
              ) : (
                <>
                  <input
                    type="text" value={appellationSearch}
                    onChange={e => { setAppellationSearch(e.target.value); setShowAppellationDrop(true); }}
                    onFocus={() => setShowAppellationDrop(true)}
                    onBlur={() => setTimeout(() => setShowAppellationDrop(false), 150)}
                    placeholder="e.g. Gevrey-Chambertin, Meursault..."
                    className="w-full px-3 py-2 rounded-md border border-border-medium bg-surface-0 text-text-primary placeholder:text-text-muted focus:outline-none focus:border-gold text-sm"
                  />
                  {showAppellationDrop && appellationResults.length > 0 && (
                    <ul className="absolute z-10 mt-1 w-full bg-surface-2 border border-border-medium rounded-md shadow-lg max-h-40 overflow-y-auto">
                      {appellationResults.slice(0, 6).map(a => (
                        <li key={a.id}>
                          <button type="button"
                            onMouseDown={() => { setSelectedAppellationId(a.id); setAppellationSearch(a.name); setShowAppellationDrop(false); }}
                            className="w-full text-left px-3 py-2 hover:bg-surface-3 text-sm">
                            <span className="text-text-primary">{a.name}</span>
                            <span className="text-text-muted ml-2 text-xs">{a.hierarchy_path}</span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              )}
            </div>

            {/* Create new appellation */}
            {!selectedAppellationId && appellationSearch.trim().length >= 2 && appellationResults.length === 0 && (
              <div className="border border-border-medium rounded-lg px-3 py-3 space-y-3 bg-surface-2">
                <p className="text-xs text-amber-400 font-medium">No appellation found — fill in details to create one:</p>
                <div>
                  <span className="block text-xs text-text-muted mb-1">Appellation name *</span>
                  <input type="text" value={newAppellationName} onChange={e => setNewAppellationName(e.target.value)}
                    placeholder={appellationSearch}
                    className="w-full px-3 py-2 rounded-md border border-border-medium bg-surface-0 text-text-primary placeholder:text-text-muted focus:outline-none focus:border-gold text-sm"
                  />
                </div>
                <div>
                  <span className="block text-xs text-text-muted mb-1">Hierarchy path (optional)</span>
                  <input type="text" value={newAppellationPath} onChange={e => setNewAppellationPath(e.target.value)}
                    placeholder="e.g. France > Burgundy > Cote de Nuits"
                    className="w-full px-3 py-2 rounded-md border border-border-medium bg-surface-0 text-text-primary placeholder:text-text-muted focus:outline-none focus:border-gold text-sm"
                  />
                </div>
              </div>
            )}

            {submitError && (
              <p className="text-xs text-red-400 bg-red-900/20 border border-red-800 rounded px-3 py-2">{submitError}</p>
            )}

            <div className="flex gap-3 pt-1">
              <button type="button" onClick={() => setWizStep(1)}
                className="flex-1 py-2 rounded-lg border border-border-medium bg-surface-2 text-text-secondary text-sm hover:border-border-strong transition-colors">
                Back
              </button>
              <button type="button"
                disabled={!selectedAppellationId && appellationSearch.trim().length < 2}
                onClick={() => { setSubmitError(''); setWizStep(3); }}
                className="flex-1 py-2 rounded-lg bg-sky-700 text-white text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-40">
                Next
              </button>
            </div>
          </div>
        )}

        {/* ── Free-text: Step 3 — Bottle details ───────────────────────── */}
        {isFreeText && wizStep === 3 && (
          <form onSubmit={handleFreeTextSubmit} className="px-5 py-4 space-y-4">
            {renderStepBar(3)}
            {renderBottleFields()}

            {submitError && (
              <p className="text-xs text-red-400 bg-red-900/20 border border-red-800 rounded px-3 py-2">{submitError}</p>
            )}

            <div className="flex gap-3 pt-1">
              <button type="button" onClick={() => setWizStep(2)}
                className="flex-1 py-2 rounded-lg border border-border-medium bg-surface-2 text-text-secondary text-sm hover:border-border-strong transition-colors">
                Back
              </button>
              <button type="submit" disabled={!vintageYear || !resolvedCellarId || isPendingAll}
                className="flex-1 py-2 rounded-lg bg-sky-700 text-white text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-40">
                {isPendingAll ? 'Saving...' : `Add wine + ${qtyNum} bottle${qtyNum !== 1 ? 's' : ''}`}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Add modal
// ---------------------------------------------------------------------------

interface AddModalProps { onClose: () => void; }

function AddModal({ onClose }: AddModalProps) {
  const { data: wines = [] } = useWines();
  const producersMap = useProducersMap();
  const createMutation = useCreateWishlistEntry();

  const [wineSearch, setWineSearch] = useState('');
  const [selectedWineId, setSelectedWineId] = useState('');
  const [vintageYear, setVintageYear] = useState('');
  const [targetPrice, setTargetPrice] = useState('');
  const [currency, setCurrency] = useState('EUR');
  const [priority, setPriority] = useState<WishlistPriority>('medium');
  const [notes, setNotes] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const filteredWines = useMemo(() => {
    const q = normalizeStr(wineSearch.trim());
    if (!q || q.length < 2) return [];
    return wines
      .filter(w => {
        const producer = producersMap.get(w.producer_id)?.name ?? '';
        const hay = normalizeStr([producer, w.cuvee_name ?? ''].join(' '));
        return hay.includes(q);
      })
      .slice(0, 8);
  }, [wines, producersMap, wineSearch]);

  const selectedWine = wines.find(w => w.id === selectedWineId);
  const selectedProducer = selectedWine ? producersMap.get(selectedWine.producer_id) : undefined;

  const isFreeText = !selectedWineId && wineSearch.trim().length >= 2;
  const canSave = !!selectedWineId || isFreeText;

  function selectWine(id: string) {
    setSelectedWineId(id);
    const w = wines.find(x => x.id === id);
    const p = w ? producersMap.get(w.producer_id) : undefined;
    setWineSearch([p?.name, w?.cuvee_name].filter(Boolean).join(' '));
    setShowDropdown(false);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSave) return;
    setSubmitError('');

    const input: WishlistCreateInput = {
      ...(selectedWineId ? { wine_id: selectedWineId } : { wine_name_free: wineSearch.trim() }),
      priority,
      ...(vintageYear && { vintage_year: parseInt(vintageYear) }),
      ...(targetPrice && { target_price: parseFloat(targetPrice), target_currency: currency }),
      ...(notes.trim() && { notes: notes.trim() }),
    };
    try {
      await createMutation.mutateAsync(input);
      onClose();
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to save — check your connection and try again.');
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog">
      <div role="button" tabIndex={0} aria-label="Close" className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClose(); } }} />
      <div className="relative z-10 w-full max-w-md bg-surface-1 border border-border-medium rounded-xl shadow-2xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border-subtle">
          <h2 className="text-sm font-semibold text-text-primary">Add to Wishlist</h2>
          <button type="button" onClick={onClose} className="text-text-muted hover:text-text-primary">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-5 py-4 space-y-4">
          {/* Wine search */}
          <div className="relative">
            <span className="block text-xs text-text-muted mb-1">Wine *</span>
            <input
              type="text"
              value={wineSearch}
              onChange={e => { setWineSearch(e.target.value); setSelectedWineId(''); setShowDropdown(true); }}
              onFocus={() => setShowDropdown(true)}
              onBlur={() => setTimeout(() => setShowDropdown(false), 150)}
              placeholder="Search producer or wine name..."
              className="w-full px-3 py-2 rounded-md border border-border-medium bg-surface-0 text-text-primary placeholder:text-text-muted focus:outline-none focus:border-gold text-sm"
            />
            {showDropdown && filteredWines.length > 0 && (
              <ul className="absolute z-10 mt-1 w-full bg-surface-2 border border-border-medium rounded-md shadow-lg max-h-48 overflow-y-auto">
                {filteredWines.map(w => {
                  const p = producersMap.get(w.producer_id);
                  return (
                    <li key={w.id}>
                      <button type="button" onMouseDown={() => selectWine(w.id)}
                        className="w-full text-left px-3 py-2 hover:bg-surface-3 text-sm">
                        <span className="text-text-primary">{w.cuvee_name}</span>
                        {p && <span className="text-text-muted ml-2 text-xs">{p.name}</span>}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
            {selectedWine && selectedProducer ? (
              <p className="mt-1 text-xs text-gold">{selectedProducer.name} — {selectedWine.cuvee_name}</p>
            ) : isFreeText && filteredWines.length === 0 ? (
              <p className="mt-1 text-xs text-amber-400">
                Not in catalog — will be saved as free-text entry. You can add it to the catalog later.
              </p>
            ) : isFreeText && filteredWines.length > 0 ? (
              <p className="mt-1 text-xs text-amber-400">
                Select from the list, or click Add to save as free-text entry.
              </p>
            ) : null}
          </div>

          {/* Vintage year */}
          <div>
            <span className="block text-xs text-text-muted mb-1">Vintage year (optional)</span>
            <input type="number" value={vintageYear} onChange={e => setVintageYear(e.target.value)}
              placeholder="e.g. 2015" min={1971} max={new Date().getFullYear()}
              className="w-full px-3 py-2 rounded-md border border-border-medium bg-surface-0 text-text-primary placeholder:text-text-muted focus:outline-none focus:border-gold text-sm" />
          </div>

          {/* Target price + currency */}
          <div className="flex gap-3">
            <div className="flex-1">
              <span className="block text-xs text-text-muted mb-1">Target price (optional)</span>
              <input type="number" value={targetPrice} onChange={e => setTargetPrice(e.target.value)}
                placeholder="e.g. 250" min={0} step={1}
                className="w-full px-3 py-2 rounded-md border border-border-medium bg-surface-0 text-text-primary placeholder:text-text-muted focus:outline-none focus:border-gold text-sm" />
            </div>
            <div className="w-24">
              <span className="block text-xs text-text-muted mb-1">Currency</span>
              <select value={currency} onChange={e => setCurrency(e.target.value)}
                className="w-full px-2 py-2 rounded-md border border-border-medium bg-surface-0 text-text-primary focus:outline-none focus:border-gold text-sm">
                <option>EUR</option><option>USD</option><option>ILS</option><option>GBP</option>
              </select>
            </div>
          </div>

          {/* Priority */}
          <div>
            <span className="block text-xs text-text-muted mb-1">Priority</span>
            <div className="flex gap-2">
              {(['high', 'medium', 'low'] as WishlistPriority[]).map(p => (
                <button key={p} type="button" onClick={() => setPriority(p)}
                  className={`flex-1 py-1.5 rounded border text-xs font-medium transition-colors ${
                    priority === p ? PRIORITY_BADGE[p].cls : 'border-border-subtle bg-surface-2 text-text-muted hover:border-border-medium'
                  }`}>
                  {PRIORITY_BADGE[p].label}
                </button>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div>
            <span className="block text-xs text-text-muted mb-1">Notes (optional)</span>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2}
              placeholder="e.g. Only in magnum, next MWC catalog..."
              className="w-full px-3 py-2 rounded-md border border-border-medium bg-surface-0 text-text-primary placeholder:text-text-muted focus:outline-none focus:border-gold text-sm resize-none" />
          </div>

          {submitError && (
            <p className="text-xs text-red-400 bg-red-900/20 border border-red-800 rounded px-3 py-2">
              {submitError}
            </p>
          )}

          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose}
              className="flex-1 py-2 rounded-lg border border-border-medium bg-surface-2 text-text-secondary text-sm hover:border-border-strong transition-colors">
              Cancel
            </button>
            <button type="submit" disabled={!canSave || createMutation.isPending}
              className="flex-1 py-2 rounded-lg bg-accent-burgundy text-white text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-40">
              {createMutation.isPending ? 'Adding...' : 'Add to Wishlist'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Entry card
// ---------------------------------------------------------------------------

interface EntryCardProps {
  entry: WishlistEntry;
  wineName: string;
  producerName: string;
  wineId: string | undefined;
  isOwner: boolean;
  onMarkAcquired: (entry: WishlistEntry) => void;
  onStatusChange: (id: string, status: WishlistStatus) => void;
  onDelete: (id: string) => void;
}

function EntryCard({ entry, wineName, producerName, wineId, isOwner, onMarkAcquired, onStatusChange, onDelete }: EntryCardProps) {
  const priority = PRIORITY_BADGE[entry.priority];
  const statusInfo = STATUS_BADGE[entry.status];
  const isFreeText = !wineId;

  return (
    <div className="bg-surface-1 border border-border-medium rounded-lg p-4 flex flex-col gap-3">
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          {wineId ? (
            <Link to={entry.vintage_year ? `/wines/${wineId}?vintage=${entry.vintage_year}` : `/wines/${wineId}`}
              className="font-medium text-text-primary text-sm hover:text-gold transition-colors truncate block">
              {wineName}
              {entry.vintage_year && <span className="ml-2 text-text-muted font-normal">{entry.vintage_year}</span>}
            </Link>
          ) : (
            <p className="font-medium text-text-primary text-sm truncate">
              {wineName}
              {entry.vintage_year && <span className="ml-2 text-text-muted font-normal">{entry.vintage_year}</span>}
            </p>
          )}
          {producerName && <p className="text-xs text-text-secondary mt-0.5">{producerName}</p>}
          {isFreeText && (
            <p className="text-xs text-text-muted mt-0.5 italic">Not yet in catalog</p>
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className={`px-2 py-0.5 rounded border text-xs font-medium ${priority.cls}`}>{priority.label}</span>
          <span className={`text-xs font-medium ${statusInfo.cls}`}>{statusInfo.label}</span>
        </div>
      </div>

      {entry.target_price && (
        <div className="flex items-center gap-2">
          <span className="text-xs text-text-muted">Target:</span>
          <span className="text-sm font-semibold text-gold tabular-nums">
            <Money value={entry.target_price} currency={entry.target_currency ?? 'EUR'} minWidthCh={6} />
          </span>
        </div>
      )}

      {entry.notes && <p className="text-xs text-text-secondary italic leading-relaxed">{entry.notes}</p>}

      {isOwner && entry.status === 'active' && (
        <div className="flex gap-2 pt-1 border-t border-border-subtle">
          <button type="button" onClick={() => onMarkAcquired(entry)}
            className="flex-1 py-1.5 rounded border border-border-medium bg-surface-2 text-sky-400 text-xs hover:border-sky-700 transition-colors">
            Mark Acquired
          </button>
          <button type="button" onClick={() => onStatusChange(entry.id, 'abandoned')}
            className="flex-1 py-1.5 rounded border border-border-medium bg-surface-2 text-text-muted text-xs hover:border-border-strong transition-colors">
            Abandon
          </button>
          <button type="button" onClick={() => onDelete(entry.id)}
            className="px-3 py-1.5 rounded border border-border-medium bg-surface-2 text-red-400 text-xs hover:border-red-700 transition-colors" title="Delete">
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

interface AcquiringEntry {
  entry: WishlistEntry;
  wineName: string;
  producerName: string;
  wineId: string | undefined;
}

export default function WishlistPage() {
  const isOwner = useAuthStore(s => s.userDoc?.role === 'owner');
  const [showAll, setShowAll] = useState(false);
  const [sortKey, setSortKey] = useState<SortKey>('priority');
  const [groupKey, setGroupKey] = useState<GroupKey>('none');
  const [showAddModal, setShowAddModal] = useState(false);
  const [acquiringEntry, setAcquiringEntry] = useState<AcquiringEntry | null>(null);

  const { data: allEntries = [], isLoading: loadingAll } = useWishlistEntries();
  const { data: activeEntries = [], isLoading: loadingActive } = useActiveWishlistEntries();
  const { data: wines = [] } = useWines();
  const producersMap = useProducersMap();
  const updateMutation = useUpdateWishlistEntry();
  const deleteMutation = useDeleteWishlistEntry();

  const isLoading = showAll ? loadingAll : loadingActive;
  const entries = showAll ? allEntries : activeEntries;

  const winesMap = useMemo(() => {
    const m = new Map<string, (typeof wines)[number]>();
    for (const w of wines) m.set(w.id, w);
    return m;
  }, [wines]);

  const enrichedEntries = useMemo(
    () => entries.map(e => {
      const wine = e.wine_id ? winesMap.get(e.wine_id) : undefined;
      const producer = wine ? producersMap.get(wine.producer_id) : undefined;
      return {
        entry: e,
        wineName: wine?.cuvee_name ?? e.wine_name_free ?? 'Unknown wine',
        producerName: producer?.name ?? '',
        wineId: e.wine_id,
      };
    }),
    [entries, winesMap, producersMap]
  );

  const sorted = useMemo(() => {
    const arr = [...enrichedEntries];
    arr.sort((a, b) => {
      switch (sortKey) {
        case 'priority': return PRIORITY_ORDER[a.entry.priority] - PRIORITY_ORDER[b.entry.priority];
        case 'target_price': return (b.entry.target_price ?? 0) - (a.entry.target_price ?? 0);
        case 'wine_name': return a.wineName.localeCompare(b.wineName);
        default: return 0;
      }
    });
    return arr;
  }, [enrichedEntries, sortKey]);

  const groups = useMemo(() => {
    if (groupKey === 'none') return [{ label: '', items: sorted }];
    const map = new Map<string, typeof sorted>();
    for (const item of sorted) {
      const key = groupKey === 'status' ? STATUS_BADGE[item.entry.status].label : item.producerName || 'Unlinked';
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(item);
    }
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b)).map(([label, items]) => ({ label, items }));
  }, [sorted, groupKey]);

  function handleMarkAcquired(e: WishlistEntry) {
    const wine = e.wine_id ? winesMap.get(e.wine_id) : undefined;
    const producer = wine ? producersMap.get(wine.producer_id) : undefined;
    setAcquiringEntry({
      entry: e,
      wineName: wine?.cuvee_name ?? e.wine_name_free ?? 'Unknown wine',
      producerName: producer?.name ?? '',
      wineId: e.wine_id,
    });
  }

  function handleStatusChange(id: string, status: WishlistStatus) {
    void updateMutation.mutateAsync({ id, input: { status } });
  }

  function handleDelete(id: string) {
    if (window.confirm('Remove this entry from your wishlist?')) {
      void deleteMutation.mutateAsync(id);
    }
  }

  const totalActive = activeEntries.length;

  return (
    <div className="min-h-screen bg-surface-0">
      <div className="sticky top-0 z-10 bg-surface-1 border-b border-border-medium px-4 py-3 flex items-center justify-between">
        <div>
          <h1 className="text-base font-semibold text-text-primary">Wishlist</h1>
          <p className="text-xs text-text-muted">{totalActive} wine{totalActive !== 1 ? 's' : ''} on the hunt</p>
        </div>
        {isOwner && (
          <button type="button" onClick={() => setShowAddModal(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-accent-burgundy text-white text-xs font-medium hover:opacity-90 transition-opacity">
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
            </svg>
            Add wine
          </button>
        )}
      </div>

      <div className="max-w-2xl mx-auto px-4 py-5">
        <div className="flex flex-wrap items-center gap-3 mb-5">
          <button type="button" onClick={() => setShowAll(v => !v)}
            className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${showAll ? 'border-border-strong bg-surface-3 text-text-primary' : 'border-border-subtle bg-surface-2 text-text-muted hover:border-border-medium'}`}>
            {showAll ? 'Showing all' : 'Active only'}
          </button>
          <div className="flex-1" />
          <select value={groupKey} onChange={e => setGroupKey(e.target.value as GroupKey)}
            className="px-2 py-1.5 rounded border border-border-medium bg-surface-0 text-text-primary text-xs focus:outline-none focus:border-gold">
            <option value="none">No grouping</option>
            <option value="producer">Group by producer</option>
            <option value="status">Group by status</option>
          </select>
          <select value={sortKey} onChange={e => setSortKey(e.target.value as SortKey)}
            className="px-2 py-1.5 rounded border border-border-medium bg-surface-0 text-text-primary text-xs focus:outline-none focus:border-gold">
            <option value="priority">Sort: Priority</option>
            <option value="date_added">Sort: Date added</option>
            <option value="target_price">Sort: Target price</option>
            <option value="wine_name">Sort: Wine name</option>
          </select>
        </div>

        {isLoading ? (
          <div className="py-16 text-center text-text-muted text-sm animate-pulse">Loading wishlist...</div>
        ) : entries.length === 0 ? (
          <div className="py-16 text-center">
            <p className="text-4xl mb-3">🔍</p>
            <p className="text-text-primary font-medium text-sm">Your wishlist is empty</p>
            <p className="text-text-muted text-xs mt-1">Add wines you're hunting for — with target prices, priorities, and notes.</p>
            {isOwner && (
              <button type="button" onClick={() => setShowAddModal(true)}
                className="mt-4 px-4 py-2 rounded-lg bg-accent-burgundy text-white text-xs font-medium hover:opacity-90 transition-opacity">
                Add your first wine
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-6">
            {groups.map(({ label, items }) => (
              <div key={label || '__all__'}>
                {label && (
                  <h2 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-3 px-1">{label}</h2>
                )}
                <div className="grid gap-3">
                  {items.map(({ entry, wineName, producerName, wineId }) => (
                    <EntryCard key={entry.id} entry={entry} wineName={wineName} producerName={producerName}
                      wineId={wineId} isOwner={isOwner}
                      onMarkAcquired={handleMarkAcquired}
                      onStatusChange={handleStatusChange} onDelete={handleDelete} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showAddModal && <AddModal onClose={() => setShowAddModal(false)} />}

      {acquiringEntry && (
        <AcquireModal
          entry={acquiringEntry.entry}
          wineName={acquiringEntry.wineName}
          producerName={acquiringEntry.producerName}
          wineId={acquiringEntry.wineId}
          onClose={() => setAcquiringEntry(null)}
        />
      )}
    </div>
  );
}
